import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AskGraphResponse {
  answer: string;
}

export interface PlaygroundNode {
  id: string;
  name: string;
  type: string;
}

export interface PlaygroundEdge {
  id: string;
  source: string;
  target: string;
  relationship: string;
}

export interface PlaygroundGraph {
  documentName: string;
  nodes: PlaygroundNode[];
  edges: PlaygroundEdge[];
}

const STOPWORDS = new Set([
  'a', 'about', 'after', 'all', 'an', 'and', 'are', 'as', 'at', 'be', 'by',
  'can', 'did', 'do', 'does', 'for', 'from', 'has', 'have', 'how', 'in', 'is',
  'it', 'its', 'of', 'on', 'or', 'our', 'that', 'the', 'their', 'this', 'to',
  'was', 'we', 'what', 'when', 'where', 'which', 'who', 'why', 'will', 'with',
  'you', 'your'
]);

interface AdjacencyEntry {
  to: string;
  edgeId: string;
  relationship: string;
}

/**
 * Client-side port of the Voyager Graph POC's playground (server/query.js):
 * loads the exact `voyager-graph.json` export bundled as a static asset and
 * answers node search / seed-finding / BFS traversal locally, since none of
 * that touches a secret. The one piece that does — generating the
 * natural-language answer — is proxied through dpe-backend's
 * KnowledgeGraphPlaygroundController (`POST {apiUrl}/knowledge-graph-playground/ask`),
 * which holds the DeepSeek key and forwards the question plus the facts this
 * service already computed from the traversal.
 */
@Injectable({ providedIn: 'root' })
export class GraphPlayground {
  private graph$?: Observable<PlaygroundGraph>;

  constructor(private http: HttpClient) {}

  getGraph(): Observable<PlaygroundGraph> {
    if (!this.graph$) {
      this.graph$ = this.http
        .get<PlaygroundGraph>('/assets/data/voyager-graph.json')
        .pipe(shareReplay(1));
    }
    return this.graph$;
  }

  searchNodes(graph: PlaygroundGraph, query: string, limit = 20): PlaygroundNode[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return graph.nodes.filter(n => n.name.toLowerCase().includes(q)).slice(0, limit);
  }

  findSeedNodes(graph: PlaygroundGraph, question: string): string[] {
    const q = question.toLowerCase();
    const words = q.split(/[^a-z0-9]+/).filter(w => w.length >= 3 && !STOPWORDS.has(w));

    const seeds: string[] = [];
    for (const n of graph.nodes) {
      const name = n.name.toLowerCase();
      if (q.includes(name) || words.some(w => name.includes(w))) {
        seeds.push(n.id);
      }
    }
    return seeds;
  }

  private buildAdjacency(graph: PlaygroundGraph): Map<string, AdjacencyEntry[]> {
    const adj = new Map<string, AdjacencyEntry[]>(graph.nodes.map(n => [n.id, []]));
    graph.edges.forEach(e => {
      adj.get(e.source)?.push({ to: e.target, edgeId: e.id, relationship: e.relationship });
      adj.get(e.target)?.push({ to: e.source, edgeId: e.id, relationship: e.relationship });
    });
    return adj;
  }

  traverse(
    graph: PlaygroundGraph,
    seedIds: string[],
    { maxDepth = 2, maxNodes = 40 } = {}
  ): { order: Array<{ nodeId: string; edgeId: string | null; depth: number }>; nodeIds: string[]; edgeIds: string[] } {
    const adj = this.buildAdjacency(graph);
    const visited = new Set<string>();
    const order: Array<{ nodeId: string; edgeId: string | null; depth: number }> = [];
    let queue: Array<{ id: string; depth: number; viaEdgeId: string | null }> =
      seedIds.map(id => ({ id, depth: 0, viaEdgeId: null }));

    while (queue.length && visited.size < maxNodes) {
      const next: typeof queue = [];
      for (const { id, depth, viaEdgeId } of queue) {
        if (visited.has(id) || visited.size >= maxNodes) continue;
        visited.add(id);
        order.push({ nodeId: id, edgeId: viaEdgeId, depth });
        if (depth < maxDepth) {
          for (const { to, edgeId } of adj.get(id) || []) {
            if (!visited.has(to)) next.push({ id: to, depth: depth + 1, viaEdgeId: edgeId });
          }
        }
      }
      queue = next;
    }

    const nodeIds = [...new Set(order.map(o => o.nodeId))];
    const edgeIds = [...new Set(order.map(o => o.edgeId).filter((id): id is string => !!id))];
    return { order, nodeIds, edgeIds };
  }

  buildFacts(graph: PlaygroundGraph, nodeIds: string[], maxFacts = 80): string[] {
    const nodeIdSet = new Set(nodeIds);
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
    const facts: string[] = [];
    for (const e of graph.edges) {
      if (nodeIdSet.has(e.source) && nodeIdSet.has(e.target)) {
        const s = nodeById.get(e.source);
        const t = nodeById.get(e.target);
        if (s && t) facts.push(`${s.name} (${s.type}) --${e.relationship}--> ${t.name} (${t.type})`);
      }
      if (facts.length >= maxFacts) break;
    }
    return facts;
  }

  askBackend(question: string, facts: string[]): Observable<AskGraphResponse> {
    return this.http.post<AskGraphResponse>(`${environment.apiUrl}/knowledge-graph-playground/ask`, { question, facts });
  }
}
